import { ApiError, apiRequest, setAuthToken, setUnauthorizedHandler } from './api.js';

export interface Tokens {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresIn: number;
}

/**
 * Why a renewal ended.
 *
 * The distinction between the last two is the entire point of this module: a
 * boolean collapsed "the network hiccuped" into "you are signed out", and the
 * recovery path deleted the refresh token, so a three-second wifi drop ended
 * presentations.
 */
export type RenewOutcome = 'renewed' | 'expired' | 'unavailable';

/** Whether the session is currently reachable. Drives the console indicator. */
export type SessionStatus = 'active' | 'reconnecting';

const REFRESH_TOKEN_KEY = 'floorpoll.refreshToken';
/** Renew this far ahead of the deadline so in-flight requests never race it. */
const RENEW_MARGIN_MS = 60_000;
/**
 * Mirrors the shape of `useLiveQuiz`'s WebSocket reconnection backoff — same
 * doubling-with-ceiling formula, same `'reconnecting'` status name — so the
 * app's two reconnection loops read alike. The `attempt` variable below is
 * named to match that one on purpose; the numbers differ because a lost
 * WebSocket and a lost token renewal do not need the same urgency.
 */
const MIN_BACKOFF_MS = 5_000;
const MAX_BACKOFF_MS = 60_000;

/**
 * Token lifecycle, deliberately outside React.
 *
 * Access and id tokens stay in memory; only the refresh token is persisted, so
 * a stolen localStorage value still has to go through Cognito to be useful.
 * Keeping the renewal timer here avoids a chicken-and-egg between "schedule a
 * renewal" and "renew" that a pair of hooks would otherwise create.
 */
let renewalTimer: ReturnType<typeof setTimeout> | null = null;
let onLost: (() => void) | null = null;
let onStatus: ((status: SessionStatus) => void) | null = null;
let sessionStatus: SessionStatus = 'active';
/** Absolute deadline. A timer handle answers "has it run?", not "am I late?". */
let expiresAt: number | null = null;
let inFlight: Promise<RenewOutcome> | null = null;
let attempt = 0;
/** Bumped by every sign-out, so a renewal in flight across one is discarded. */
let generation = 0;

export function onSessionLost(callback: (() => void) | null) {
  onLost = callback;
}

export function onSessionStatus(callback: ((status: SessionStatus) => void) | null) {
  onStatus = callback;
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function adoptTokens(tokens: Tokens) {
  // The API's JWT authorizer validates the id token — it carries the email claim.
  setAuthToken(tokens.idToken);
  if (tokens.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  expiresAt = Date.now() + tokens.expiresIn * 1000;
  attempt = 0;
  publishStatus('active');
  scheduleRenewal();
}

export function clearSession() {
  clearTimer();
  generation += 1;
  inFlight = null;
  expiresAt = null;
  attempt = 0;
  // Assigned rather than published: a sign-out is a reset, not a reconnection,
  // and subscribers must not read it as "the network came back".
  //
  // The obligation this creates: publishStatus() only fires when the value
  // changes, so priming sessionStatus back to 'active' here means the next
  // adoptTokens() may find nothing to publish — if the last broadcast status
  // was 'reconnecting', its publishStatus('active') is a no-op, and this
  // module never emits the event that says the network is back. A subscriber
  // that mirrors sessionStatus — Task 5's React state, for instance — must
  // therefore reset its own copy to 'active' on sign-out itself; it cannot
  // wait for an event that this module will not send.
  sessionStatus = 'active';
  setAuthToken(null);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Renews using the stored refresh token.
 *
 * Concurrent callers share one request. The console fires several requests at
 * once, so a single stale token would otherwise produce a burst of refreshes.
 */
export function renewSession(): Promise<RenewOutcome> {
  inFlight ??= attemptRenewal().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function attemptRenewal(): Promise<RenewOutcome> {
  const issued = generation;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return 'expired';

  try {
    const tokens = await apiRequest<Tokens>('/auth/refresh', {
      method: 'POST',
      body: { refreshToken },
      anonymous: true,
    });
    // A sign-out that landed while this was in flight wins. Adopting here
    // would silently sign a signed-out user back in.
    //
    // Reusing 'expired' for this rather than adding a fourth outcome works
    // because 'expired' means "superseded" here, not "the credential was
    // rejected" — and every consumer of 'expired' already treats it as
    // "there is no session to act on", which is exactly true once a sign-out
    // has landed. That equivalence is load-bearing: a future outcome that
    // needs to tell the two apart cannot just reuse this branch.
    if (issued !== generation) return 'expired';
    // Cognito omits the refresh token on renewal; keep the one we have.
    adoptTokens({ ...tokens, refreshToken: tokens.refreshToken ?? refreshToken });
    return 'renewed';
  } catch (error) {
    // Same race, failing side — see the success-side comment above for why
    // reusing 'expired' ("superseded") is safe here too: a signed-out session
    // must not keep retrying.
    if (issued !== generation) return 'expired';
    if (isCredentialRejected(error)) {
      clearSession();
      return 'expired';
    }
    // Unreachable, not over. Keeping the refresh token here is the fix: this
    // is where a wifi blip used to destroy a 90-day credential.
    scheduleRetry();
    return 'unavailable';
  }
}

/**
 * True when the server rejected the credential itself rather than failing to
 * answer. `translateCognitoError` maps a dead refresh token to 401; 429 and
 * every 5xx mean Cognito is unavailable, and status 0 means the network is.
 *
 * Anything that is not an ApiError — a malformed body, say — is treated as
 * transient, because a wrong 'unavailable' costs a delayed sign-out while a
 * wrong 'expired' costs the presentation.
 */
function isCredentialRejected(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status >= 400 && error.status < 500 && error.status !== 429;
}

function publishStatus(next: SessionStatus) {
  if (sessionStatus === next) return;
  sessionStatus = next;
  onStatus?.(next);
}

function clearTimer() {
  if (renewalTimer) clearTimeout(renewalTimer);
  renewalTimer = null;
}

function scheduleRenewal() {
  clearTimer();
  if (expiresAt === null) return;
  // A delay of 0 is intentional, not a bug: the old code floored this at 30s
  // because expiresIn was all it had to work with. expiresAt is an absolute
  // timestamp, so a deadline that already falls inside the margin — a tab
  // that was asleep, say — must renew immediately rather than wait out a
  // floor. Task 4's wake-up catch-up depends on that; do not reintroduce one.
  const delay = Math.max(expiresAt - RENEW_MARGIN_MS - Date.now(), 0);
  renewalTimer = setTimeout(() => void renewAndReportLoss(), delay);
}

function scheduleRetry() {
  clearTimer();
  publishStatus('reconnecting');
  const delay = Math.min(MIN_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
  attempt += 1;
  renewalTimer = setTimeout(() => void renewAndReportLoss(), delay);
}

/**
 * Renews and reports a definitive expiry. Every timer and listener uses this
 * rather than `renewSession` directly.
 *
 * `renewSession` already collapses concurrent callers onto one request, but
 * without the `joined` check below every one of those callers would still
 * report loss independently — three parallel 401s is the documented normal
 * case (`api.ts`'s handler is called once per in-flight request), and it
 * would turn one lost session into a burst of `onSessionLost` calls. Harmless
 * today because `signOut` is idempotent, but a future non-idempotent listener
 * — a toast, an analytics event, a history push — would fire once per caller
 * instead of once per session.
 */
async function renewAndReportLoss(): Promise<RenewOutcome> {
  const joined = inFlight !== null;
  const outcome = await renewSession();
  if (outcome === 'expired' && !joined) onLost?.();
  return outcome;
}

// Registered at import. `api.ts` cannot import this module without closing a
// cycle, so the dependency is inverted the way `setAuthToken` already is.
setUnauthorizedHandler(async () => (await renewAndReportLoss()) === 'renewed');

/** True when the deadline has passed or is inside the renewal margin. */
function isPastDue(): boolean {
  return expiresAt !== null && Date.now() >= expiresAt - RENEW_MARGIN_MS;
}

/**
 * Deliberately not gated on `document.visibilityState`. A console is often the
 * background tab during a presentation, and `online` firing in a hidden tab is
 * exactly the case worth acting on.
 */
const catchUp = () => {
  if (isPastDue()) void renewAndReportLoss();
};

/**
 * Starts the wake-up listeners, because the timer alone cannot be trusted:
 * browsers throttle it in background tabs and a sleeping machine never fires
 * it at all.
 *
 * Registered from the auth provider rather than at import so that importing
 * this module never touches `window`.
 */
export function startSessionWatch() {
  document.addEventListener('visibilitychange', catchUp);
  window.addEventListener('online', catchUp);
}

export function stopSessionWatch() {
  document.removeEventListener('visibilitychange', catchUp);
  window.removeEventListener('online', catchUp);
}
